/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDateRange from 'components/MDDateRange';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

import stepperName from './TaxInvoiceStatus';
import TaxInvoiceStepper from './TaxInvoiceStepper';
import SearchSaleOrder from './SearchSaleOrder';
import SearchCustomer from './SearchCustomer';

const initAttr = {
  id: '',
  code: '',
  so_id: '',
  so_code: '',
  company_id: '1',
  company_name: '',
  customer_id: '',
  customer_name: '',
  customer_address: '',
  customer_tax_id: '',
  customer_branch_code: '',
  customer_branch_name: '',
  status: 'request',
  nettotal: 0,
  date_record: '',
  print: '',
  printed_at: '',
  printed_name: '',
  can_print: '1',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  status: 'ALL',
  period: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
};

const initNavigate = { first: '', prev: '', next: '', last: '' };

function TaxInvoice() {
  const mod = 'tax_invoice';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const { id } = useParams();

  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [stepper, setStepper] = useState({});
  const [navigate, setNavigate] = useState(initNavigate);
  const [find, setFind] = useState(initFind);
  const [record, setRecord] = useState([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalPrint, setModalPrint] = useState(false);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState({});
  const [notify, setNotify] = useState(null);
  const [cboStatus, setCboStatus] = useState([]);
  const [cboCompany, setCboCompany] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center', width: 160 },
    { id: 'tb.code', label: 'เลขใบกำกับ', align: 'center' },
    { id: 'so.code', label: 'เลขบิลขาย', align: 'center' },
    { id: 'tb.customer_name', label: 'ชื่อลูกค้า', align: 'left' },
    { id: 'tb.customer_tax_id', label: 'เลขผู้เสียภาษี', align: 'left' },
    { id: 'tb.created_at', label: 'วันที่สร้าง', align: 'center' },
    { id: 'tb.printed_at', label: 'ผู้พิมพ์', align: 'center' },
  ];

  const showNotify = (msg, color = 'success') => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = (autoPrint = false) => {
    setLoading(true);
    setAttr(initAttr);
    setList([]);

    FetchData('GET', `/${mod}/${id}`, app.token, { lang: app.lang }).then((result) => {
      if (result.status === 'ok') {
        const data = {
          ...result.data,
          company_id: String(result.data.company_id || '1'),
        };
        setAttr(data);
        setList(result.list || []);
        setNavigate(result.navigate);
        setStepper(result.stepper || {});
        if (autoPrint) {
          FetchData('OPTIONS', `/${mod}/print`, app.token, {
            id: data.id,
            lang: app.lang,
          }).then((printResult) => {
            if (printResult.status === 'ok') {
              setAttr({
                ...data,
                print: printResult.data.print,
                printed_at: printResult.data.printed_at,
                printed_name: printResult.data.printed_name,
                can_print: '0',
              });
              setModalPrint(true);
            } else {
              showNotify(printResult.msg || 'พิมพ์ไม่สำเร็จ', 'error');
            }
          });
        }
      }
      setLoading(false);
    });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, { lang: app.lang }).then((result) => {
      if (result.status === 'ok') {
        setCboStatus([
          { id: 'ALL', name: 'ทั้งหมด' },
          { id: 'ACTIVE', name: 'ใช้งาน' },
          ...(result.stepper || []),
        ]);
        setCboCompany(result.company || []);
        setCboBranch(result.branch || []);
      }
    });
  };

  const add = (item) => {
    setLoading(true);
    FetchData('PUT', `/${mod}`, app.token, { so_id: item.so_id }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        window.location.replace(`/${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(result.msg || t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = () => {
    if (!Func.verifyLength(String(attr.company_id || ''), 1)) {
      showNotify('กรุณาเลือกสาขาบริษัท', 'error');
      return;
    }
    if (!Func.verifyLength(attr.customer_name, 1)) {
      showNotify('กรุณากรอกชื่อลูกค้า', 'error');
      return;
    }

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData('PATCH', `/${mod}/${id}`, app.token, {
          company_id: attr.company_id,
          customer_id: attr.customer_id,
          customer_name: attr.customer_name,
          customer_address: attr.customer_address,
          customer_tax_id: attr.customer_tax_id,
          customer_branch_code: attr.customer_branch_code,
          customer_branch_name: attr.customer_branch_name,
        }).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
            open();
          } else {
            showNotify(result.msg || t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: `คุณต้องการเปลี่ยนสถานะเป็น \n"${stepperName[status].name}" ใช่หรือไม่ ?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id,
          status,
        }).then((result) => {
          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
            open(status === 'success');
          } else {
            setLoading(false);
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const print = () => {
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: attr.id,
      lang: app.lang,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setAttr({
          ...attr,
          print: result.data.print,
          printed_at: result.data.printed_at,
          printed_name: result.data.printed_name,
          can_print: '0',
        });
        setModalPrint(true);
      } else {
        showNotify(result.msg || 'พิมพ์ไม่สำเร็จ', 'error');
      }
    });
  };

  const selectCustomer = (item) => {
    setAttr({ ...attr, ...item });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      period: 'DATE',
      page: 1,
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  useEffect(() => {
    init();
    if (id === undefined) {
      view(find);
    } else {
      open();
    }
  }, [id]);

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const btnOpen = (item) => (
    <NavLink to={`/${mod}/${item.id}`}>
      <MDButton
        variant='text'
        color='info'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
      >
        {item.code}
      </MDButton>
    </NavLink>
  );

  const renderSearch = () => (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, period: 'ALL', page: 1 });
            }}
          >
            <MDInput
              name='searchform'
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MDInput
            fullWidth
            value={find.status}
            select
            onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <span>สถานะ :</span>
                </InputAdornment>
              ),
            }}
          >
            {cboStatus.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        {find.period === 'ALL' ? (
          <Grid item xs={12} sm={12} md={6} lg={2}>
            <MDButton
              variant='outlined'
              color='secondary'
              fullWidth
              sx={{ p: 1.5 }}
              onClick={() => view({ ...find, period: 'DATE', page: 1 })}
            >
              <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
            </MDButton>
          </Grid>
        ) : null}
        {find.period === 'ALL' ? null : (
          <Grid item xs={12} sm={12} md={12} lg={5}>
            <MDDateRange
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );

  const renderList = () => (
    <MDBox mb={7}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={9} md={10} lg={11}>
          {renderSearch()}
        </Grid>
        <Grid item xs={12} sm={3} md={2} lg={1}>
          <SearchSaleOrder callback={add} branch={cboBranch} />
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ minHeight: 400 }}>
            <Table aria-label='datatable table'>
              <TableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleSort}
                data={initHeader}
              />
              <TableBody>
                {record.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align='center' width={160}>
                      {Func.statusDisplay(row)}
                    </TableCell>
                    <TableCell align='center'>{btnOpen(row)}</TableCell>
                    <TableCell align='center' className='nowrap'>
                      {row.so_code}
                    </TableCell>
                    <TableCell align='left'>
                      <MDBox className='cut' width={200}>
                        {row.customer_name}
                      </MDBox>
                    </TableCell>
                    <TableCell align='left' className='nowrap'>
                      {row.customer_tax_id || '-'}
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      {Func.dateTimeDisplay(row.created_at)}
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      {row.printed_name ? (
                        <>
                          {row.printed_name}
                          <br />
                          {Func.dateTimeDisplay(row.printed_at)}
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'request':
          return key === 'edit' || key === 'success';
        case 'edit':
          return key === 'success';
        default:
          return false;
      }
    };

    const stepLevel = (key) => (
      <Step sx={{ minWidth: 150 }}>
        <StepLabel StepIconComponent={TaxInvoiceStepper}>
          {action(key) ? (
            <MDButton
              variant='gradient'
              color={stepperName[key].color}
              size='small'
              onClick={() => approve(key)}
            >
              <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
            </MDButton>
          ) : stepper[key] === undefined ? (
            <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
          ) : attr.status === key ? (
            <MDBox>
              <MDBadge
                badgeContent={stepperName[key].name}
                variant='contained'
                color={stepperName[key].color}
                size='md'
                container
              />
              <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
              <MDTypography variant='subtitle2'>
                {Func.dateTimeDisplay(stepper[key].created_at)}
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
              <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
              <MDTypography variant='subtitle2'>
                {Func.dateTimeDisplay(stepper[key].created_at)}
              </MDTypography>
            </MDBox>
          )}
        </StepLabel>
      </Step>
    );

    const stepInt = () => {
      if (attr.status === 'edit') return 1;
      if (attr.status === 'success') return 2;
      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('request')}
          {stepLevel('edit')}
          {stepLevel('success')}
        </Stepper>
      </MDBox>
    );
  };

  const renderNavigate = () => (
    <Card sx={{ p: 2 }}>
      <Grid container>
        <Grid item xs={6}>
          <NavLink to={`/${mod}`}>
            <MDButton variant='contained' color='dark'>
              <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
            </MDButton>
          </NavLink>
        </Grid>
        {navigate.first === '' ? null : (
          <Grid item xs={6}>
            <MDBox display='flex' justifyContent='flex-end' gap={1}>
              <NavLink to={`/${mod}/${navigate.first}`}>
                <MDButton
                  variant='contained'
                  color='dark'
                  iconOnly
                  disabled={String(navigate.first) === String(id)}
                >
                  <Icon>first_page</Icon>
                </MDButton>
              </NavLink>
              <NavLink to={`/${mod}/${navigate.prev}`}>
                <MDButton
                  variant='contained'
                  color='dark'
                  iconOnly
                  disabled={String(navigate.prev) === String(id)}
                >
                  <Icon>navigate_before</Icon>
                </MDButton>
              </NavLink>
              <NavLink to={`/${mod}/${navigate.next}`}>
                <MDButton
                  variant='contained'
                  color='dark'
                  iconOnly
                  disabled={String(navigate.next) === String(id)}
                >
                  <Icon>navigate_next</Icon>
                </MDButton>
              </NavLink>
              <NavLink to={`/${mod}/${navigate.last}`}>
                <MDButton
                  variant='contained'
                  color='dark'
                  iconOnly
                  disabled={String(navigate.last) === String(id)}
                >
                  <Icon>last_page</Icon>
                </MDButton>
              </NavLink>
            </MDBox>
          </Grid>
        )}
      </Grid>
    </Card>
  );

  const renderButton = () => (
    <MDBox display='flex' justifyContent='end' gap={1}>
      {attr.status === 'request' || attr.status === 'edit' ? (
        <MDButton variant='gradient' color='success' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      ) : null}
      {attr.status === 'success' && attr.can_print !== '0' ? (
        <MDButton variant='gradient' color='info' onClick={print}>
          <Icon>print</Icon>&nbsp;พิมพ์
        </MDButton>
      ) : null}
    </MDBox>
  );

  const readOnly = attr.status === 'success';
  const customerReadOnly = attr.status !== 'edit';

  const renderInfo = () => (
    <MDBox className='card' p={2}>
      <MDTypography variant='h5' mb={3}>
        ข้อมูลเอกสาร
      </MDTypography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDSelect
            label='สาขาบริษัทที่ออกใบ *'
            labelDefault={t(`element.please_select`)}
            data={cboCompany}
            value={attr.company_id}
            onChange={(e) => setAttr({ ...attr, company_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='เลขบิลขาย'
            value={attr.so_code || ''}
            variant='standard'
            fullWidth
            disabled
            sx={{ backgroundColor: '#ffffff !important' }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='วันที่ขาย'
            value={Func.dateDisplay(attr.date_record)}
            variant='standard'
            fullWidth
            disabled
            sx={{ backgroundColor: '#ffffff !important' }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ยอดรวม'
            value={Func.numberDisplay(attr.nettotal)}
            variant='standard'
            fullWidth
            disabled
            sx={{ backgroundColor: '#ffffff !important' }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
              inputProps: { className: 'text-right' },
            }}
          />
        </Grid>
        {attr.printed_name ? (
          <Grid item xs={12}>
            <MDInput
              label='ผู้พิมพ์'
              value={`${attr.printed_name}  ${Func.dateTimeDisplay(attr.printed_at)}`}
              variant='standard'
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        ) : null}
      </Grid>
    </MDBox>
  );

  const renderCustomer = () => (
    <MDBox className='card' p={2}>
      <Grid container>
        <Grid item xs={8}>
          <MDTypography variant='h5' mb={3}>
            ข้อมูลผู้เสียภาษี
          </MDTypography>
        </Grid>
        <Grid item xs={4}>
          {attr.status === 'edit' ? (
            <MDBox display='flex' justifyContent='end'>
              <SearchCustomer callback={selectCustomer} />
            </MDBox>
          ) : null}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อลูกค้า *'
            value={attr.customer_name || ''}
            onChange={(e) => setAttr({ ...attr, customer_name: e.target.value })}
            variant='standard'
            fullWidth
            inputProps={{ readOnly: customerReadOnly, maxLength: 255 }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='เลขประจำตัวผู้เสียภาษี'
            value={attr.customer_tax_id || ''}
            onChange={(e) => setAttr({ ...attr, customer_tax_id: Func.maskIdCard(e.target.value) })}
            variant='standard'
            fullWidth
            inputProps={{ readOnly: customerReadOnly, maxLength: 20 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัสสาขา'
            value={attr.customer_branch_code || ''}
            onChange={(e) => setAttr({ ...attr, customer_branch_code: e.target.value })}
            variant='standard'
            fullWidth
            inputProps={{ readOnly: customerReadOnly, maxLength: 10 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='ชื่อสาขา'
            value={attr.customer_branch_name || ''}
            onChange={(e) => setAttr({ ...attr, customer_branch_name: e.target.value })}
            variant='standard'
            fullWidth
            inputProps={{ readOnly: customerReadOnly, maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ที่อยู่'
            value={attr.customer_address || ''}
            onChange={(e) => setAttr({ ...attr, customer_address: e.target.value })}
            variant='standard'
            multiline
            fullWidth
            inputProps={{ readOnly: customerReadOnly }}
          />
        </Grid>
      </Grid>
    </MDBox>
  );

  const renderItems = () => (
    <MDBox className='card'>
      <MDBox p={2}>
        <MDTypography variant='h5'>รายการสินค้า</MDTypography>
      </MDBox>
      <TableContainer>
        <Table aria-label='items table'>
          <TableHeader
            order='asc'
            orderBy=''
            onRequestSort={() => {}}
            data={[
              { id: 'no', label: '#', align: 'center', sort: false, width: 50 },
              { id: 'image', label: 'รูป', align: 'center', sort: false, width: 40 },
              { id: 'code', label: 'รหัส', align: 'left', sort: false },
              { id: 'name', label: 'สินค้า', align: 'left', sort: false },
              { id: 'quantity', label: 'จำนวน', align: 'right', sort: false },
              { id: 'price', label: 'ราคา', align: 'right', sort: false },
              { id: 'amount', label: 'รวม', align: 'right', sort: false },
            ]}
          />
          <TableBody>
            {list.map((row, i) => (
              <TableRow key={row.id} hover>
                <TableCell align='center'>{i + 1}</TableCell>
                <TableCell
                  align='center'
                  onClick={() => {
                    setPhoto(row);
                    setModalPhoto(true);
                  }}
                >
                  {Func.imageDisplay(row)}
                </TableCell>
                <TableCell align='left' className='nowrap'>
                  {row.code}
                </TableCell>
                <TableCell align='left'>
                  {row.name}
                  {row.barcode ? ` (${row.barcode})` : ''}
                </TableCell>
                <TableCell align='right' className='nowrap'>
                  {Func.numberDisplay(row.quantity)}
                </TableCell>
                <TableCell align='right' className='nowrap'>
                  {Func.numberDisplay(row.price)}
                </TableCell>
                <TableCell align='right' className='nowrap'>
                  {Func.numberDisplay(row.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );

  const renderPhoto = () => (
    <Dialog
      open={modalPhoto}
      maxWidth='sm'
      keepMounted
      fullWidth
      onClose={() => setModalPhoto(false)}
    >
      <DialogTitle>
        {photo.code} <span className='color-default'>{photo.sku}</span>
        <IconButton
          aria-label='close'
          onClick={() => setModalPhoto(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox mb={1}>
          <MDTypography variant='body'>{photo.name}</MDTypography>
        </MDBox>
        <MDBox
          component='img'
          src={photo.image}
          alt=''
          borderRadius='lg'
          width='100%'
          position='relative'
          sx={{ boxShadow: 0 }}
        />
      </DialogContent>
    </Dialog>
  );

  const renderPrint = () => (
    <Dialog
      open={modalPrint}
      maxWidth='xl'
      keepMounted
      fullScreen
      onClose={() => {
        setModalPrint(false);
        open();
      }}
    >
      <DialogTitle>
        #{attr.code}
        <IconButton
          aria-label='close'
          onClick={() => setModalPrint(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {attr.print ? (
          <Iframe
            url={attr.print}
            width='100%'
            height='100%'
            id='print'
            className=''
            display='initial'
            position='relative'
            frameBorder='0'
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );

  const renderOpen = () => (
    <MDBox mb={7}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderNavigate()}
        </Grid>
        <Grid item xs={12}>
          {renderStepper()}
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} xl={6}>
                <MDTypography variant='h3'>#{attr.code}</MDTypography>
              </Grid>
              <Grid item xs={12} xl={6}>
                {renderButton()}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                {renderInfo()}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={8}>
                {renderCustomer()}
              </Grid>
              <Grid item xs={12}>
                {renderItems()}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.sale`), t(`menu.tax_invoice`)]} />
      {id === undefined ? renderList() : renderOpen()}
      {renderPhoto()}
      {renderPrint()}
      {renderLoading()}
      {notify}
      <Footer />
    </DashboardLayout>
  );
}

export default TaxInvoice;
